'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'convex/react';
import { useLocale } from 'next-intl';
import { useIsPresent } from 'framer-motion';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { FloatingVoiceDock } from '@/components/voice/floating-voice-dock';
import { useRealtimeVoiceCallController } from '@/components/org-chat/use-realtime-voice-call-controller';
import { useRealtimeVoiceThreadDispatch } from '@/components/org-chat/use-realtime-voice-thread-dispatch';
import type { OrgChatQueueItem } from '@/components/org-chat/org-chat-queue-controls';
import type { PersistentVoiceCall } from '@/lib/persistent-voice-call-state';
import {
  buildRealtimeVoicePageContext,
  formatRealtimeVoicePageContext,
  type RealtimeVoicePageMetadata,
} from '@/lib/realtime-voice-page-context';
import { getCanonicalThreadPath } from '@/lib/thread-routes';
import { getRealtimeVoicePendingQuestionRevision } from '@/lib/realtime-voice-pending-question-revision';

type Props = {
  call: PersistentVoiceCall;
  pathname: string;
  pageMetadata: RealtimeVoicePageMetadata | null;
  onEnd: () => void;
};

export function PersistentVoiceCallSession(props: Props) {
  const { onEnd } = props;
  const orgThreadId = props.call.orgThreadId as Id<'orgThreads'>;
  const thread = useQuery(api.organizations.threads.queries.getById, {
    id: orgThreadId,
  });
  useEffect(() => {
    if (thread === null) onEnd();
  }, [thread, onEnd]);
  if (!thread) return null;
  return (
    <ConnectedVoiceCall {...props} orgThreadId={orgThreadId} thread={thread} />
  );
}

function ConnectedVoiceCall({
  call,
  pathname,
  pageMetadata,
  onEnd,
  orgThreadId,
  thread,
}: Props & {
  orgThreadId: Id<'orgThreads'>;
  thread: NonNullable<
    ReturnType<
      typeof useQuery<typeof api.organizations.threads.queries.getById>
    >
  >;
}) {
  const locale = useLocale();
  const isPresent = useIsPresent();
  const levelsRef = useRef({ user: 0, agent: 0 });
  const delegationTarget = useRealtimeVoiceThreadDispatch({
    orgThreadId,
    orgId: call.organizationId as Id<'organizations'>,
  });
  const pageContext = useMemo(() => {
    const context = buildRealtimeVoicePageContext({
      pathname,
      sourceOrgSlug: call.orgSlug,
      sourceThreadId: call.orgThreadId,
      pageMetadata,
    });
    return context ? formatRealtimeVoicePageContext(context) : null;
  }, [pathname, call.orgSlug, call.orgThreadId, pageMetadata]);
  const realtimeStatus =
    thread.realtimeVoiceStatusText && thread.realtimeVoiceStatusTurnId
      ? {
          text: thread.realtimeVoiceStatusText,
          turnId: thread.realtimeVoiceStatusTurnId,
          sessionId: thread.realtimeVoiceStatusSessionId,
          createdAt: thread.realtimeVoiceStatusCreatedAt,
        }
      : null;
  const controller = useRealtimeVoiceCallController({
    open: isPresent,
    onClose: onEnd,
    sessionId: call.sessionId,
    orgThreadId,
    orgSlug: call.orgSlug,
    organizationId: call.organizationId as Id<'organizations'>,
    delegationTarget,
    queuedMessages: (thread.messageQueue ?? []) as OrgChatQueueItem[],
    realtimeStatus,
    pendingQuestionRevision: getRealtimeVoicePendingQuestionRevision(thread),
    levelsRef,
    pageContext,
    isSourceChatVisible:
      pathname === getCanonicalThreadPath(call.orgSlug, call.routeRef),
    floating: true,
  });
  return (
    <FloatingVoiceDock
      locale={locale}
      status={controller.status}
      isEnding={controller.isEnding}
      inputMode={controller.inputMode}
      micMuted={controller.micMuted}
      pttActive={controller.pttActive}
      canPressToSpeak={
        controller.status === 'listening' || controller.status === 'speaking'
      }
      isSourceChatVisible={
        pathname === getCanonicalThreadPath(call.orgSlug, call.routeRef)
      }
      sourceThreadHref={getCanonicalThreadPath(call.orgSlug, call.routeRef)}
      sourceThreadTitle={thread.title ?? call.title}
      callElapsedMs={controller.callElapsedMs}
      errorText={controller.errorText ?? controller.setupError}
      onToggleMute={() => controller.setMicMuted((muted) => !muted)}
      onInputModeChange={(mode) => {
        controller.setPttActive(false);
        controller.setIsCapturing(false);
        controller.setMicMuted(false);
        controller.setInputMode(mode);
      }}
      onPressToSpeakStart={() => {
        controller.setPttActive(true);
        controller.setIsCapturing(true);
      }}
      onPressToSpeakEnd={() => {
        controller.setPttActive(false);
        controller.setIsCapturing(false);
      }}
      onEnd={controller.handleEnd}
    />
  );
}
